<template>
  <div class="sc-wrap">

    <!-- Nav row -->
    <div class="sc-wnav-row">
      <button v-if="viewMode !== 'C'" class="sc-wbtn" @click="prev">&#8249;</button>

      <div class="sc-nav-label-wrap">
        <template v-if="rangeStart && viewMode !== 'C' && viewMode !== 'Q' && viewMode !== 'Y' && viewMode !== 'D'">
          <span class="sc-range-pill sc-range-pill--start">{{ fmtShort(rangeStart) }}</span>
          <span class="sc-range-arrow">→</span>
          <span class="sc-range-pill" :class="rangeEnd ? 'sc-range-pill--end' : 'sc-range-pill--empty'">{{ rangeEnd ? fmtShort(rangeEnd) : '?' }}</span>
          <button v-if="rangeEnd" class="sc-range-x" @click.stop="clearRange">✕</button>
        </template>
        <template v-else>
          <span class="sc-wlabel">{{ viewMode === 'C' ? 'Custom' : navLabel }}</span>
        </template>
      </div>

      <button v-if="viewMode !== 'C'" class="sc-wbtn" @click="next">&#8250;</button>

      <div class="sc-nav-right">
        <template v-if="viewMode === 'C'">
          <input type="date" class="sc-date-input" v-model="customFrom" />
          <span class="sc-range-sep">–</span>
          <input type="date" class="sc-date-input" v-model="customTo" />
        </template>
        <div class="sc-view-wrap">
          <select class="sc-view-select" :value="viewMode" @change="setViewMode($event.target.value)">
            <option v-for="v in viewModes" :key="v.k" :value="v.k">{{ v.l }}</option>
          </select>
          <svg class="sc-view-arr" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
        </div>
        <button class="sc-refresh-btn" @click="resetAll" title="Clear filters">
          <svg viewBox="0 0 24 24"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
        </button>
      </div>
    </div>

    <!-- ===== DAY VIEW ===== -->
    <div v-if="viewMode === 'D'" class="sc-day-view">
      <div class="sc-day-summary">
        <div v-for="chip in chips" :key="chip.key" class="sc-day-kpi">
          <div class="sc-day-kpi-val" :style="{ color: chip.color }">{{ dayCount(dayStart, chip.key) }}</div>
          <div class="sc-day-kpi-lbl">{{ chip.label }}</div>
        </div>
      </div>
    </div>

    <!-- ===== WEEK VIEW ===== -->
    <div v-else-if="viewMode === 'W'" class="sc-grid">
      <div v-for="day in weekDays" :key="day.date" class="sc-day"
        :class="{
          today:         day.isToday,
          selected:      !rangeEnd && selectedDate === day.date,
          'sc-rs':       day.date === rangeStart,
          'sc-re':       day.date === rangeEnd,
          'sc-in-range': isInRange(day.date)
        }"
        @click="selectDay(day.date)">
        <div class="sc-day-hdr">
          <span class="sc-dow">{{ day.dow }}</span>
          <span class="sc-dd" :class="{ 'sc-dd-today': day.isToday }">{{ day.dd }}</span>
        </div>
        <div class="sc-stacks">
          <div v-for="chip in chips" :key="chip.key"
            v-if="dayCount(day.date, chip.key) > 0"
            class="sc-st"
            :style="{ background: chip.color, height: stackH(day.date, chip.key) + 'px' }"
            :title="chip.label + ': ' + dayCount(day.date, chip.key)"
            @click.stop="filterByChip(day.date, chip)">
            <span v-if="stackH(day.date, chip.key) >= 16" class="sc-stv">{{ dayCount(day.date, chip.key) }}</span>
          </div>
        </div>
        <div class="sc-day-total" v-if="dayTotal(day.date) > 0">{{ dayTotal(day.date) }}</div>
      </div>
    </div>

    <!-- ===== MONTH VIEW ===== -->
    <div v-else-if="viewMode === 'M'" class="sc-mgrid">
      <div class="sc-mhdr" v-for="h in ['Mon','Tue','Wed','Thu','Fri','Sat','Sun']" :key="h">{{ h }}</div>
      <div v-for="(cell, i) in monthCells" :key="'mc' + i" class="sc-mcell"
        :class="{
          'sc-mcell-empty':  !cell,
          'sc-mcell-today':  cell && cell.isToday,
          'sc-mcell-sel':    cell && !rangeEnd && selectedDate === cell.date,
          'sc-rs':           cell && cell.date === rangeStart,
          'sc-re':           cell && cell.date === rangeEnd,
          'sc-in-range':     cell && isInRange(cell.date)
        }"
        @click="cell ? selectDay(cell.date) : null">
        <template v-if="cell">
          <span class="sc-mdd" :class="{ 'sc-mdd-today': cell.isToday }">{{ cell.dd }}</span>
          <div class="sc-mdots">
            <div v-for="chip in chips" :key="chip.key"
              v-if="dayCount(cell.date, chip.key) > 0"
              class="sc-mdot"
              :style="{ background: chip.color }"
              :title="chip.label + ': ' + dayCount(cell.date, chip.key)"
              @click.stop="filterByChip(cell.date, chip)"></div>
          </div>
          <div class="sc-mday-total" v-if="dayTotal(cell.date) > 0">{{ dayTotal(cell.date) }}</div>
        </template>
      </div>
    </div>

    <!-- ===== QUARTER VIEW ===== -->
    <div v-else-if="viewMode === 'Q'" class="sc-q-grid">
      <div v-for="q in fourQuarters" :key="q.label" class="sc-q-box" @click="drillToMonth(q.start)">
        <div class="sc-q-top">
          <span class="sc-q-label">{{ q.label }}</span>
          <span class="sc-q-range">{{ q.range }}</span>
        </div>
        <div class="sc-q-bars">
          <div v-for="chip in chips" :key="chip.key"
            v-if="q.counts[chip.key] > 0"
            class="sc-q-bar"
            :style="{ background: chip.color, width: aggPct(q.counts[chip.key], maxQCount) + '%' }">
            <span class="sc-agg-val">{{ q.counts[chip.key] }}</span>
          </div>
          <div v-if="!chips.some(c => q.counts[c.key] > 0)" class="sc-q-empty">No data</div>
        </div>
        <div class="sc-q-total">{{ q.total }}</div>
      </div>
    </div>

    <!-- ===== YEAR VIEW ===== -->
    <div v-else-if="viewMode === 'Y'" class="sc-agg-list">
      <div v-for="yr in recentYears" :key="yr.label" class="sc-agg-row" @click="drillToQuarterYear(yr.year)">
        <span class="sc-agg-lbl">{{ yr.label }}</span>
        <div class="sc-agg-bars">
          <div v-for="chip in chips" :key="chip.key"
            v-if="yr.counts[chip.key] > 0"
            class="sc-agg-bar"
            :style="{ background: chip.color, width: aggPct(yr.counts[chip.key], maxYCount) + '%' }">
            <span class="sc-agg-val">{{ yr.counts[chip.key] }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== CUSTOM VIEW ===== -->
    <div v-else-if="viewMode === 'C'" class="sc-agg-list">
      <div v-for="(row, i) in customRangeDays" :key="'cr' + i" class="sc-agg-row" @click="selectDay(row.date)">
        <span class="sc-agg-lbl">{{ row.label }}</span>
        <div class="sc-agg-bars">
          <div v-for="chip in chips" :key="chip.key"
            v-if="dayCount(row.date, chip.key) > 0"
            class="sc-agg-bar"
            :style="{ background: chip.color, width: aggPct(dayCount(row.date, chip.key), maxCCount) + '%' }">
            <span class="sc-agg-val">{{ dayCount(row.date, chip.key) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== SELECTED DAY detail ===== -->
    <div v-if="selectedDate && !rangeEnd && viewMode !== 'Q' && viewMode !== 'Y'" class="sc-hourly-full">
      <div class="sc-panel-hdr">
        <span class="sc-panel-date">{{ selectedDateFmt }}</span>
        <span class="sc-panel-type" style="color:#7C5CFC">Tasks</span>
        <button class="sc-filter-btn" @click="applyCurrentFilter">Filter day ↗</button>
      </div>
      <div class="sc-day-task-list">
        <div v-for="t in selectedDayTasks" :key="t.id" class="sc-dtask-row">
          <span class="sc-dtask-dot" :style="{ background: taskChipColor(t) }"></span>
          <span class="sc-dtask-title">{{ t.title || t.content_title || ('#' + t.id) }}</span>
          <span class="sc-dtask-status">{{ taskStatusLabel(t) }}</span>
        </div>
        <div v-if="!selectedDayTasks.length" class="sc-chart-empty">No tasks this day</div>
      </div>
    </div>

  </div>
</template>

<script>
import moment from 'moment/moment';

const YEAR_RANGE = 5;

const CHIPS = [
  { key: 'active', label: 'Active',  color: '#fa8100' },
  { key: 'done',   label: 'Done',    color: '#2D9D5E' },
  { key: 'missed', label: 'Missed',  color: '#E05A3A' },
];

export default {
  name: 'C1TaskSmartCalendar',
  props: {
    tasks: { type: Array, default: () => [] },
  },

  data() {
    return {
      viewMode:    'W',
      viewModes:   [
        { k: 'D', l: 'Day' },
        { k: 'W', l: 'Week' },
        { k: 'M', l: 'Month' },
        { k: 'Q', l: 'Quarter' },
        { k: 'Y', l: 'Year' },
        { k: 'C', l: 'Custom' },
      ],
      chips:       CHIPS,
      dayStart:    moment(),
      weekStart:   moment().startOf('isoWeek'),
      monthStart:  moment().startOf('month'),
      quarterYear: moment().year(),
      customFrom:  moment().subtract(7, 'days').format('YYYY-MM-DD'),
      customTo:    moment().format('YYYY-MM-DD'),
      selectedDate: null,
      rangeStart:  null,
      rangeEnd:    null,
    };
  },

  computed: {
    navLabel() {
      switch (this.viewMode) {
        case 'D': return this.dayStart.format('ddd, DD MMM YYYY');
        case 'W': return this.weekStart.format('DD MMM') + ' – ' + this.weekStart.clone().endOf('isoWeek').format('DD MMM YYYY');
        case 'M': return this.monthStart.format('MMMM YYYY');
        case 'Q': return String(this.quarterYear);
        case 'Y': return (moment().year() - YEAR_RANGE + 1) + ' – ' + moment().year();
        default:  return '';
      }
    },

    selectedDateFmt() { return this.selectedDate ? moment(this.selectedDate).format('ddd, DD MMM') : ''; },

    // Compute per-date stats from tasks prop
    dayStats() {
      const now = moment();
      const stats = {};
      const set = (date, key) => {
        if (!stats[date]) stats[date] = { active: 0, done: 0, missed: 0 };
        stats[date][key]++;
      };
      (this.tasks || []).forEach(t => {
        const due = t.due_date ? moment(t.due_date).format('YYYY-MM-DD') : null;
        const start = t.start_date ? moment(t.start_date).format('YYYY-MM-DD') : null;
        const completedAt = t.completed_at || t.updated_at;
        const isDone = !!(t.is_completed || t.completed_at);

        if (isDone && completedAt) {
          set(moment(completedAt).format('YYYY-MM-DD'), 'done');
        } else if (due) {
          const isPast = moment(due).isBefore(now, 'day');
          set(due, isPast ? 'missed' : 'active');
        } else if (start) {
          set(start, 'active');
        }
      });
      return stats;
    },

    weekDays() {
      const today = moment().format('YYYY-MM-DD');
      return Array.from({ length: 7 }, (_, i) => {
        const d = this.weekStart.clone().add(i, 'days');
        const date = d.format('YYYY-MM-DD');
        return { date, dow: d.format('ddd').toUpperCase(), dd: d.format('D'), isToday: date === today };
      });
    },

    monthCells() {
      const cells = [];
      const start = this.monthStart.clone().startOf('month');
      const end   = start.clone().endOf('month');
      const today = moment().format('YYYY-MM-DD');
      const offset = start.isoWeekday() - 1;
      for (let i = 0; i < offset; i++) cells.push(null);
      let d = start.clone();
      while (d.isSameOrBefore(end, 'day')) {
        const date = d.format('YYYY-MM-DD');
        cells.push({ date, dd: d.date(), isToday: date === today });
        d.add(1, 'day');
      }
      while (cells.length % 7 !== 0) cells.push(null);
      return cells;
    },

    fourQuarters() {
      return [1, 2, 3, 4].map(q => {
        const qStart = moment().year(this.quarterYear).quarter(q).startOf('quarter');
        const qEnd   = qStart.clone().endOf('quarter');
        const counts = { active: 0, done: 0, missed: 0 };
        let d = qStart.clone();
        while (d.isSameOrBefore(qEnd, 'day')) {
          const date = d.format('YYYY-MM-DD');
          if (this.dayStats[date]) {
            counts.active += this.dayStats[date].active || 0;
            counts.done   += this.dayStats[date].done   || 0;
            counts.missed += this.dayStats[date].missed  || 0;
          }
          d.add(1, 'day');
        }
        const total = counts.active + counts.done + counts.missed;
        return { label: 'Q' + q, range: qStart.format('MMM') + '–' + qEnd.format('MMM'), start: qStart.format('YYYY-MM-DD'), counts, total };
      });
    },

    maxQCount() {
      let max = 1;
      this.fourQuarters.forEach(q => CHIPS.forEach(c => { if (q.counts[c.key] > max) max = q.counts[c.key]; }));
      return max;
    },

    recentYears() {
      const current = moment().year();
      return Array.from({ length: YEAR_RANGE }, (_, i) => {
        const y = current - i;
        const yStart = moment().year(y).startOf('year');
        const yEnd   = yStart.clone().endOf('year');
        const counts = { active: 0, done: 0, missed: 0 };
        let d = yStart.clone();
        const cap = moment();
        while (d.isSameOrBefore(yEnd, 'day') && d.isSameOrBefore(cap, 'day')) {
          const date = d.format('YYYY-MM-DD');
          if (this.dayStats[date]) {
            counts.active += this.dayStats[date].active || 0;
            counts.done   += this.dayStats[date].done   || 0;
            counts.missed += this.dayStats[date].missed  || 0;
          }
          d.add(1, 'day');
        }
        return { label: String(y), year: y, start: yStart.format('YYYY-MM-DD'), counts };
      });
    },

    maxYCount() {
      let max = 1;
      this.recentYears.forEach(y => CHIPS.forEach(c => { if (y.counts[c.key] > max) max = y.counts[c.key]; }));
      return max;
    },

    customRangeDays() {
      if (!this.customFrom || !this.customTo) return [];
      const days = [];
      let d = moment(this.customFrom);
      const end = moment(this.customTo);
      while (d.isSameOrBefore(end, 'day')) {
        days.push({ date: d.format('YYYY-MM-DD'), label: d.format('DD MMM') });
        d.add(1, 'day');
        if (days.length > 60) break;
      }
      return days;
    },

    maxCCount() {
      let max = 1;
      this.customRangeDays.forEach(r => CHIPS.forEach(c => { const v = this.dayCount(r.date, c.key); if (v > max) max = v; }));
      return max;
    },

    selectedDayTasks() {
      if (!this.selectedDate) return [];
      const date = this.selectedDate;
      const now = moment();
      return (this.tasks || []).filter(t => {
        const due = t.due_date ? moment(t.due_date).format('YYYY-MM-DD') : null;
        const start = t.start_date ? moment(t.start_date).format('YYYY-MM-DD') : null;
        const isDone = !!(t.is_completed || t.completed_at);
        const completedDate = (t.completed_at || t.updated_at) ? moment(t.completed_at || t.updated_at).format('YYYY-MM-DD') : null;
        return (isDone && completedDate === date) || (!isDone && (due === date || start === date));
      }).slice(0, 10);
    },
  },

  methods: {
    setViewMode(v) {
      this.viewMode = v;
      this.selectedDate = null;
      this.rangeStart = null;
      this.rangeEnd = null;
      if (v === 'D') { this.dayStart = moment(); this.selectedDate = this.dayStart.format('YYYY-MM-DD'); }
    },

    prev() {
      if      (this.viewMode === 'D') this.dayStart   = this.dayStart.clone().subtract(1, 'day');
      else if (this.viewMode === 'W') this.weekStart  = this.weekStart.clone().subtract(7, 'days');
      else if (this.viewMode === 'M') this.monthStart = this.monthStart.clone().subtract(1, 'month');
      else if (this.viewMode === 'Q') this.quarterYear -= 1;
      else if (this.viewMode === 'Y') return;
      this.clearRange();
      this.selectedDate = this.viewMode === 'D' ? this.dayStart.format('YYYY-MM-DD') : null;
    },

    next() {
      if      (this.viewMode === 'D') this.dayStart   = this.dayStart.clone().add(1, 'day');
      else if (this.viewMode === 'W') this.weekStart  = this.weekStart.clone().add(7, 'days');
      else if (this.viewMode === 'M') this.monthStart = this.monthStart.clone().add(1, 'month');
      else if (this.viewMode === 'Q') this.quarterYear += 1;
      else if (this.viewMode === 'Y') return;
      this.clearRange();
      this.selectedDate = this.viewMode === 'D' ? this.dayStart.format('YYYY-MM-DD') : null;
    },

    drillToMonth(dateStr) {
      this.monthStart = moment(dateStr).startOf('month');
      this.viewMode = 'M'; this.clearRange(); this.selectedDate = null;
    },

    drillToQuarterYear(year) {
      this.quarterYear = year;
      this.viewMode = 'Q'; this.clearRange(); this.selectedDate = null;
    },

    selectDay(date) {
      if (!this.rangeStart) {
        this.rangeStart = date;
        this.rangeEnd = null;
        this.selectedDate = date;
      } else if (!this.rangeEnd) {
        const a = date < this.rangeStart ? date : this.rangeStart;
        const b = date < this.rangeStart ? this.rangeStart : date;
        this.rangeStart = a;
        this.rangeEnd = b;
        this.selectedDate = null;
        this.$nextTick(() => this.applyRangeFilter());
      } else {
        this.rangeStart = date;
        this.rangeEnd = null;
        this.selectedDate = date;
      }
    },

    isInRange(date) {
      if (!this.rangeStart || !this.rangeEnd) return false;
      return date > this.rangeStart && date < this.rangeEnd;
    },

    clearRange() {
      this.rangeStart = null;
      this.rangeEnd = null;
      this.selectedDate = null;
    },

    applyRangeFilter() {
      if (!this.rangeStart || !this.rangeEnd) return;
      this.$emit('daySelected', { date: this.rangeStart, dateTo: this.rangeEnd });
    },

    applyCurrentFilter() {
      if (this.selectedDate) this.$emit('daySelected', { date: this.selectedDate, dateTo: this.selectedDate });
    },

    filterByChip(date, chip) {
      this.$emit('daySelected', { date, dateTo: date, chipKey: chip.key });
    },

    resetAll() {
      this.clearRange();
      this.$emit('reset');
    },

    fmtShort(date) { return moment(date).format('DD MMM'); },

    dayCount(date, key) { return (this.dayStats[date] && this.dayStats[date][key]) || 0; },

    dayTotal(date) {
      if (!this.dayStats[date]) return 0;
      return (this.dayStats[date].active || 0) + (this.dayStats[date].done || 0) + (this.dayStats[date].missed || 0);
    },

    stackH(date, key) {
      const cnt = this.dayCount(date, key);
      if (!cnt) return 0;
      let max = 0;
      this.weekDays.forEach(d => { const v = this.dayCount(d.date, key); if (v > max) max = v; });
      return max ? Math.max(8, Math.round((cnt / max) * 44)) : 0;
    },

    aggPct(count, max) { return Math.round((count / (max || 1)) * 90); },

    taskChipColor(t) {
      const isDone = !!(t.is_completed || t.completed_at);
      if (isDone) return '#2D9D5E';
      if (t.due_date && moment(t.due_date).isBefore(moment(), 'day')) return '#E05A3A';
      return '#fa8100';
    },

    taskStatusLabel(t) {
      if (t.is_completed || t.completed_at) return 'Done';
      if (t.due_date && moment(t.due_date).isBefore(moment(), 'day')) return 'Missed';
      return 'Active';
    },
  },
};
</script>

<style scoped>
.sc-wrap {
  background: var(--c1-card, #FFF);
  font-family: 'DM Sans', -apple-system, sans-serif;
  font-size: 12px; color: #1A1A1A;
  display: flex; flex-direction: column;
  overscroll-behavior: contain;
}

/* NAV ROW */
.sc-wnav-row {
  display: flex; align-items: center; gap: 5px;
  padding: 6px 8px;
  border-bottom: 1px solid #E4E3DF;
  background: #fff; flex-shrink: 0;
  min-height: 36px;
}
.sc-nav-label-wrap { flex: 1; display: flex; align-items: center; gap: 4px; min-width: 0; overflow: hidden; }
.sc-nav-right { display: flex; align-items: center; gap: 4px; flex-shrink: 0; }
.sc-wlabel { font-size: 11px; font-weight: 700; color: #1A1A1A; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.sc-wbtn { width: 22px; height: 22px; border-radius: 5px; border: 1px solid #E4E3DF; background: #FAFAF8; cursor: pointer; font-size: 15px; line-height: 1; display: grid; place-items: center; color: #6B6963; padding: 0; flex-shrink: 0; transition: background .1s; }
.sc-wbtn:hover { background: #F0EEE8; }
.sc-range-pill { font-size: 10px; font-weight: 700; padding: 2px 7px; border-radius: 4px; }
.sc-range-pill--start { background: rgba(124,92,252,.12); color: #7C5CFC; }
.sc-range-pill--end   { background: rgba(124,92,252,.22); color: #7C5CFC; }
.sc-range-pill--empty { background: #F5F5F3; color: #A09E98; }
.sc-range-arrow { font-size: 11px; color: #A09E98; }
.sc-range-x { background: none; border: none; cursor: pointer; color: #A09E98; font-size: 12px; padding: 0 2px; }
.sc-range-sep { font-size: 11px; color: #A09E98; flex-shrink: 0; }
.sc-view-wrap { position: relative; flex-shrink: 0; }
.sc-view-select { border: 1px solid #E4E3DF; border-radius: 5px; background: #FAFAF8; font-size: 10px; font-weight: 600; color: #1A1A1A; padding: 3px 18px 3px 6px; cursor: pointer; appearance: none; -webkit-appearance: none; }
.sc-view-arr { position: absolute; right: 4px; top: 50%; transform: translateY(-50%); width: 9px; height: 9px; pointer-events: none; color: #A09E98; }
.sc-refresh-btn { width: 22px; height: 22px; border-radius: 5px; border: 1px solid #E4E3DF; background: #FAFAF8; cursor: pointer; display: grid; place-items: center; padding: 0; flex-shrink: 0; color: #6B6963; }
.sc-refresh-btn svg { width: 11px; height: 11px; fill: none; stroke: currentColor; stroke-width: 2.5; stroke-linecap: round; }
.sc-date-input { border: 1px solid #E4E3DF; border-radius: 5px; background: #FAFAF8; font-size: 10px; color: #1A1A1A; padding: 3px 5px; cursor: pointer; max-width: 100px; }

/* WEEK GRID */
.sc-grid { display: grid; grid-template-columns: repeat(7,1fr); gap: 3px; padding: 6px 8px 8px; }
.sc-day { border: 1px solid #EEEDEA; border-radius: 7px; padding: 4px 3px; display: flex; flex-direction: column; align-items: center; gap: 3px; cursor: pointer; transition: background .12s, border-color .12s; background: #FAFAF8; min-width: 0; }
.sc-day:hover { background: rgba(124,92,252,.05); border-color: rgba(124,92,252,.25); }
.sc-day.today { border-color: rgba(124,92,252,.35); background: rgba(124,92,252,.05); }
.sc-day.selected { border-color: #7C5CFC; background: rgba(124,92,252,.1); }
.sc-day.sc-rs, .sc-day.sc-re { border-color: #7C5CFC !important; background: rgba(124,92,252,.15) !important; }
.sc-day.sc-in-range { background: rgba(124,92,252,.06); border-color: rgba(124,92,252,.2); }
.sc-day-hdr { display: flex; flex-direction: column; align-items: center; gap: 1px; width: 100%; }
.sc-dow { font-size: 8px; font-weight: 600; color: #A09E98; letter-spacing: .5px; }
.sc-dd { font-size: 11px; font-weight: 700; color: #6B6963; }
.sc-dd-today { color: #7C5CFC; }
.sc-stacks { display: flex; align-items: flex-end; gap: 2px; height: 52px; width: 100%; justify-content: center; }
.sc-st { border-radius: 2px 2px 0 0; min-width: 8px; flex: 1; max-width: 18px; display: flex; align-items: flex-end; justify-content: center; transition: height .3s ease; }
.sc-stv { font-size: 8px; font-weight: 700; color: #fff; padding-bottom: 2px; }
.sc-day-total { font-size: 9px; font-weight: 700; color: #A09E98; }

/* MONTH GRID */
.sc-mgrid { display: grid; grid-template-columns: repeat(7,1fr); gap: 2px; padding: 6px 8px 8px; }
.sc-mhdr { font-size: 8px; font-weight: 700; color: #A09E98; text-align: center; padding: 2px 0; letter-spacing: .5px; }
.sc-mcell { border: 1px solid #EEEDEA; border-radius: 5px; padding: 4px 3px; min-height: 40px; cursor: pointer; background: #FAFAF8; display: flex; flex-direction: column; align-items: center; gap: 2px; transition: background .12s; }
.sc-mcell:hover { background: rgba(124,92,252,.05); border-color: rgba(124,92,252,.2); }
.sc-mcell-empty { background: transparent; border-color: transparent; cursor: default; }
.sc-mcell-today { border-color: rgba(124,92,252,.4); background: rgba(124,92,252,.06); }
.sc-mcell-sel { border-color: #7C5CFC !important; background: rgba(124,92,252,.12) !important; }
.sc-mcell.sc-rs, .sc-mcell.sc-re { border-color: #7C5CFC !important; background: rgba(124,92,252,.15) !important; }
.sc-mcell.sc-in-range { background: rgba(124,92,252,.05); border-color: rgba(124,92,252,.15); }
.sc-mdd { font-size: 10px; font-weight: 700; color: #6B6963; }
.sc-mdd-today { color: #7C5CFC; }
.sc-mdots { display: flex; gap: 2px; flex-wrap: wrap; justify-content: center; }
.sc-mdot { width: 6px; height: 6px; border-radius: 50%; }
.sc-mday-total { font-size: 8px; color: #A09E98; font-weight: 700; }

/* QUARTER */
.sc-q-grid { display: grid; grid-template-columns: repeat(2,1fr); gap: 6px; padding: 6px 8px 8px; }
.sc-q-box { border: 1px solid #EEEDEA; border-radius: 8px; padding: 8px 10px; cursor: pointer; background: #FAFAF8; transition: background .12s; }
.sc-q-box:hover { background: rgba(124,92,252,.05); border-color: rgba(124,92,252,.2); }
.sc-q-top { display: flex; justify-content: space-between; margin-bottom: 6px; }
.sc-q-label { font-size: 12px; font-weight: 800; color: #1A1A1A; }
.sc-q-range { font-size: 9px; color: #A09E98; }
.sc-q-bars { display: flex; flex-direction: column; gap: 3px; }
.sc-q-bar { height: 14px; border-radius: 3px; display: flex; align-items: center; min-width: 20px; }
.sc-q-empty { font-size: 9px; color: #A09E98; }
.sc-q-total { font-size: 10px; font-weight: 700; color: #6B6963; margin-top: 4px; }

/* YEAR / CUSTOM */
.sc-agg-list { display: flex; flex-direction: column; gap: 4px; padding: 6px 8px 8px; }
.sc-agg-row { display: flex; align-items: center; gap: 8px; cursor: pointer; }
.sc-agg-lbl { font-size: 10px; font-weight: 700; color: #6B6963; width: 40px; flex-shrink: 0; }
.sc-agg-bars { display: flex; flex-direction: column; gap: 2px; flex: 1; }
.sc-agg-bar { height: 12px; border-radius: 3px; display: flex; align-items: center; min-width: 16px; }
.sc-agg-val { font-size: 8px; font-weight: 700; color: #fff; padding: 0 4px; }

/* DAY VIEW */
.sc-day-view { padding: 8px; }
.sc-day-summary { display: flex; gap: 8px; }
.sc-day-kpi { flex: 1; text-align: center; background: #FAFAF8; border: 1px solid #EEEDEA; border-radius: 7px; padding: 8px 4px; }
.sc-day-kpi-val { font-size: 18px; font-weight: 800; }
.sc-day-kpi-lbl { font-size: 9px; color: #A09E98; margin-top: 2px; }

/* SELECTED DAY TASKS */
.sc-hourly-full { border-top: 1px solid #EEEDEA; padding: 8px; }
.sc-panel-hdr { display: flex; align-items: center; gap: 6px; margin-bottom: 6px; }
.sc-panel-date { font-size: 11px; font-weight: 700; color: #1A1A1A; }
.sc-panel-type { font-size: 10px; font-weight: 700; }
.sc-filter-btn { margin-left: auto; background: rgba(124,92,252,.1); border: 1px solid rgba(124,92,252,.3); border-radius: 5px; color: #7C5CFC; font-size: 10px; font-weight: 700; padding: 3px 8px; cursor: pointer; }
.sc-filter-btn:hover { background: rgba(124,92,252,.18); }
.sc-day-task-list { display: flex; flex-direction: column; gap: 4px; max-height: 150px; overflow-y: auto; }
.sc-dtask-row { display: flex; align-items: center; gap: 6px; padding: 4px 6px; border-radius: 5px; background: #FAFAF8; border: 1px solid #EEEDEA; }
.sc-dtask-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
.sc-dtask-title { flex: 1; font-size: 11px; color: #1A1A1A; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.sc-dtask-status { font-size: 9px; font-weight: 700; color: #A09E98; flex-shrink: 0; }
.sc-chart-empty { font-size: 11px; color: #A09E98; text-align: center; padding: 8px; }
</style>
